import { Button, ConfigProvider, Select } from 'antd';
import generatePicker from 'antd/es/date-picker/generatePicker';
import locale from 'antd/es/locale/en_GB';
import classNames from 'classnames';
import type { RangeValue } from 'rc-picker/es/interface';
import { useEffect, useMemo, useState } from 'react';

import { dateGenerateConfig } from 'app/components/fields/dateGenerateConfig';
import type { Period, PeriodToCompare, Unit } from 'app/typings/analytics';
import { formatDateWithoutHour, rangeValueIsNotNull } from 'app/utils/dates';
import { amplitudeTrack } from 'app/utils/track/amplitude';

import {
  formatDateText,
  getPeriodsDates,
  getRangeValue,
  getUnitFromRange,
} from '../../commons/Utils';

import './DashboardHeader.scss';
import { DashboardMenu } from './DashboardMenu';

const { Option } = Select;

const DatePicker = generatePicker<Date>(dateGenerateConfig);
const { RangePicker } = DatePicker;

type DashboardHeaderProps = {
  setDateRange: (dateRange: [Date, Date]) => void;
  dateRange: [Date, Date];
  setUnit: (unit: Unit) => void;
  unit: Unit;
  setPeriod: (period: Period) => void;
  period: Period;
  setPeriodToCompare: (periodToCompare: PeriodToCompare) => void;
  periodToCompare: PeriodToCompare;
  menu: { label: string; key: string }[];
  saveScroll: () => void;
};

const UNIT_OPTIONS = [
  {
    value: 'DAILY',
    label: 'Daily',
  },
  {
    value: 'WEEKLY',
    label: 'Weekly',
  },
  {
    value: 'MONTHLY',
    label: 'Monthly',
  },
  {
    value: 'YEARLY',
    label: 'Yearly',
  },
] as const satisfies readonly { value: Unit; label: string }[];

const PERIODS_OPTIONS = [
  {
    value: 'custom',
    label: 'Custom',
  },
  {
    value: 'last7Days',
    label: 'Last 7 days',
  },
  {
    value: 'last4Weeks',
    label: 'Last 4 weeks',
  },
  {
    value: 'last3Months',
    label: 'Last 3 months',
  },
  {
    value: 'last12Months',
    label: 'Last 12 months',
  },
] as const satisfies readonly { value: Period; label: string }[];

const COMPARE_TO_OPTIONS = [
  {
    value: 'previousPeriod',
    label: 'Previous period',
  },
  {
    value: 'lastYear',
    label: 'Last Year',
  },
] as const satisfies readonly { value: PeriodToCompare; label: string }[];

export const DashboardHeader = ({
  setDateRange,
  dateRange,
  setUnit,
  unit,
  setPeriod,
  period,
  setPeriodToCompare,
  periodToCompare,
  menu,
  saveScroll,
}: DashboardHeaderProps) => {
  const [pendingRangeValue, setPendingRangeValue] =
    useState<[Date, Date]>(dateRange);
  const [openRangePicker, setOpenRangePicker] = useState(false);

  const unitOptions = useMemo(
    () => getUnitFromRange(period, [...UNIT_OPTIONS], dateRange),
    [period, dateRange]
  );

  useEffect(() => {
    if (
      !unitOptions.some((option) => option.value === unit) &&
      unitOptions[0].value
    ) {
      setUnit(unitOptions[0].value);
    }
  }, [dateRange, setUnit, unitOptions, unit]);

  const onChangePeriod = (newPeriod: Period) => {
    onChangeDashboardConfig(() => {
      amplitudeTrack('Change quick action', { value: newPeriod });

      setPeriod(newPeriod);
      setDateRange(getRangeValue(unit, newPeriod));
    });
  };

  const onChangeUnit = (newUnit: Unit) => {
    onChangeDashboardConfig(() => {
      amplitudeTrack('Change unit', { value: newUnit });
      setUnit(newUnit);

      setDateRange(getRangeValue(newUnit, period, dateRange));
    });
  };

  const onChangeRange = (newRange: RangeValue<Date>) => {
    if (rangeValueIsNotNull(newRange)) {
      onChangeDashboardConfig(() => {
        setPendingRangeValue(newRange);
      });
    }
  };

  const applyCustomDates = () => {
    amplitudeTrack('Use custom dates', {
      startDate: formatDateWithoutHour(pendingRangeValue[0]) || '',
      endDate: formatDateWithoutHour(pendingRangeValue[1]) || '',
    });
    setPeriod('custom');
    setOpenRangePicker(false);

    setDateRange(getRangeValue(unit, 'custom', pendingRangeValue));
  };

  const onChangeCompareTo = (newCompareTo: PeriodToCompare) => {
    onChangeDashboardConfig(() => {
      amplitudeTrack('Change compare to', { value: newCompareTo });
      setPeriodToCompare(newCompareTo);
    });
  };

  const onChangeDashboardConfig = (func: () => void) => {
    func();
    saveScroll();
  };

  return (
    <div className="dashboardHeader">
      <div className="customizeDashboard">
        <div className="leftPart">
          <Select
            optionLabelProp="label"
            optionFilterProp="children"
            className="headerSelect period"
            onChange={onChangePeriod}
            value={period}
          >
            {PERIODS_OPTIONS.map((periodOption) => (
              <Option
                key={periodOption.value}
                value={periodOption.value}
                label={periodOption.label}
              >
                <div className="optionDashboardItem">
                  <div
                    className={classNames([
                      'optionValue',
                      { selected: periodOption.value === period },
                    ])}
                  >
                    {periodOption.label}
                  </div>
                </div>
              </Option>
            ))}
          </Select>
          <ConfigProvider locale={locale}>
            <RangePicker
              className="periodRange"
              format="ddd DD MMM YY"
              onChange={onChangeRange}
              value={dateRange}
              allowClear={false}
              renderExtraFooter={() => (
                <div className="rangeFooter">
                  <Button type="primary" onClick={applyCustomDates}>
                    Apply
                  </Button>
                </div>
              )}
              open={openRangePicker}
              onOpenChange={() => {
                setOpenRangePicker(true);
              }}
              onBlur={() => setOpenRangePicker(false)}
            />
          </ConfigProvider>
        </div>
        <span>compare to</span>
        <div className="rightPart">
          <Select
            optionLabelProp="label"
            optionFilterProp="children"
            onChange={onChangeCompareTo}
            value={periodToCompare}
            className="headerSelect unit"
            dropdownMatchSelectWidth={false}
            dropdownStyle={{ width: '380px' }}
          >
            {COMPARE_TO_OPTIONS.map((compare) => (
              <Option
                key={compare.value}
                value={compare.value}
                label={compare.label}
              >
                <div className="optionDashboardItem">
                  <div
                    className={classNames([
                      'optionValue',
                      { selected: compare.value === periodToCompare },
                    ])}
                  >
                    {compare.label}
                  </div>
                  <div className="previousDates">
                    {formatDateText(
                      getPeriodsDates(dateRange, unit, compare.value)
                        .datesPreviousPeriod
                    )}
                  </div>
                </div>
              </Option>
            ))}
          </Select>

          <Select
            optionLabelProp="label"
            optionFilterProp="children"
            onChange={onChangeUnit}
            value={unit}
            className="headerSelect unit"
          >
            {unitOptions.map((unitOption) => (
              <Option
                key={unitOption.value}
                value={unitOption.value}
                label={unitOption.label}
              >
                <div className="optionDashboardItem">
                  <div
                    className={classNames([
                      'optionValue',
                      { selected: unitOption.value === unit },
                    ])}
                  >
                    {unitOption.label}
                  </div>
                </div>
              </Option>
            ))}
          </Select>
        </div>
      </div>
      <div className="dashboardNavigation">
        <DashboardMenu menu={menu} />
      </div>
    </div>
  );
};
